import { uniq } from 'lodash';
import React, { useState, useEffect } from 'react';
import { FormGroup } from '@blueprintjs/core';
import {
  MultiSelect,
  ItemRenderer,
  ItemPredicate,
  ItemListRenderer
} from '@blueprintjs/select';
import { useField, Markup, MarkupProps } from '@lightbeamhealth/blueprint-form';

import { Menu, MenuItem } from '../menu';
import { ITag, TagType, useGetAllTags } from '../../../libs/tag';

type TagItem = Partial<ITag> & {
  value: string;
};

interface TagsInputProps extends MarkupProps {
  placeholder?: string;
  type?: TagType;
  maxItems?: number;
  minItems?: number;
}

const TagMultiSelect = MultiSelect.ofType<TagItem>();

const GetTagItemsFromTagsValue = (value: string): TagItem[] => {
  const tags: TagItem[] = [];
  if (value && value.length > 0) {
    const parts = uniq(value.split(','));
    for (const part of parts) {
      tags.push({
        value: part
      });
    }
  }
  return tags;
};

const TagsInput = ({
  label,
  labelElement,
  name,
  placeholder,
  fill,
  large,
  disabled,
  type,
  minItems,
  maxItems
}: TagsInputProps): JSX.Element => {
  // Setup the field
  const id = Markup.getID();
  const [field, meta, helpers] = useField<string>({
    name: name,
    validate: (value: string) => {
      let error: string = null;
      if (minItems || maxItems) {
        const items = value.split(',').filter((item) => item !== '');
        if (items.length < minItems) {
          if (minItems === 1) {
            error = 'At least 1 tag is required!';
          } else {
            error = `At least ${minItems} tags are required!`;
          }
        }
        if (items.length > maxItems) {
          if (maxItems === 1) {
            error = 'Only 1 tag can be selected!';
          } else {
            error = `Only ${maxItems} tags can be selected!`;
          }
        }
      }
      return error;
    }
  });
  const initialTags: TagItem[] = GetTagItemsFromTagsValue(field.value);
  const [tagsSelected, setTagsSelected] = useState<TagItem[]>(initialTags);
  const { tags } = useGetAllTags({
    type
  });
  const isTagEqual = (a: TagItem, b: TagItem): boolean => {
    return a.value.trim() === b.value.trim();
  };
  const isTagInArray = (tag: TagItem, tags: TagItem[]): boolean => {
    return tags.some((_tag) => isTagEqual(tag, _tag));
  };
  const filterTag: ItemPredicate<TagItem> = (
    query,
    tag,
    _index,
    exactMatch
  ) => {
    const normalizedValue = tag.value.trim().toLowerCase();
    const normalizedQuery = query.trim().toLowerCase();
    if (exactMatch) {
      return normalizedQuery === normalizedValue;
    }
    return normalizedValue.includes(normalizedQuery);
  };
  const selectTag = (tag: TagItem) => {
    if (isTagInArray(tag, tagsSelected)) {
      return;
    }
    const tags = [...tagsSelected, tag];
    setTagsSelected(tags);
    helpers.setTouched(true);
  };
  const deselectTag = (value: string) => {
    const tags = tagsSelected.filter(
      (tag) => tag.value.trim() !== value.trim()
    );
    setTagsSelected(tags);
    helpers.setTouched(true);
  };
  const renderTags: ItemListRenderer<TagItem> = ({
    items,
    itemsParentRef,
    query,
    renderItem
  }) => {
    const renderedItems = items.map(renderItem).filter((item) => item != null);
    return (
      <Menu
        ulRef={itemsParentRef}
        style={{
          maxHeight: '340px',
          overflowY: 'auto'
        }}
      >
        {renderedItems.length === 0 && !query && (
          <li>
            <span className="bp5-menu-item">
              <span className="bp5-text-overflow-ellipsis bp5-fill">
                No tags left.
              </span>
            </span>
          </li>
        )}
        {query && (
          <MenuItem
            disabled={true}
            text={`Found ${renderedItems.length} tags matching "${query}".`}
          />
        )}
        {renderedItems}
      </Menu>
    );
  };
  const renderTagOption: ItemRenderer<TagItem> = (
    tag,
    { modifiers, handleClick }
  ) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    if (isTagInArray(tag, tagsSelected)) {
      return null;
    }
    return (
      <MenuItem
        key={tag.id}
        text={tag.value}
        shouldDismissPopover={false}
        onClick={handleClick}
      />
    );
  };
  // Update the actual field on selection change
  useEffect(() => {
    helpers.setValue(tagsSelected.map((tag) => tag.value.trim()).join(','));
  }, [tagsSelected]);
  useEffect(() => {
    const tags = GetTagItemsFromTagsValue(field.value);
    setTagsSelected(tags);
  }, [field.value]);
  return (
    <FormGroup
      label={labelElement || label}
      intent={meta.touched && meta.error ? 'danger' : 'none'}
      helperText={meta.touched ? meta.error : null}
    >
      <TagMultiSelect
        tagInputProps={{
          intent: meta.touched && meta.error ? 'danger' : 'none',
          inputProps: {
            'aria-label': label,
            autoComplete: 'off'
          },
          onRemove: deselectTag,
          large: large,
          disabled: disabled,
          placeholder: placeholder
        }}
        popoverProps={{
          position: 'auto'
        }}
        itemListRenderer={renderTags}
        itemRenderer={renderTagOption}
        itemPredicate={filterTag}
        noResults={<MenuItem disabled={true} text="No Tags Matched" />}
        tagRenderer={(tag) => tag.value}
        items={tags}
        selectedItems={tagsSelected}
        itemsEqual={isTagEqual}
        onItemSelect={selectTag}
        fill={fill}
        resetOnSelect
      />
      <input id={id} type="hidden" name={name} value={field.value} />
    </FormGroup>
  );
};

export default TagsInput;
